import React, { useState } from 'react';
import { DownloadIcon, LinkedinIcon, MailIcon, PhoneIcon, MapPinIcon, CheckIcon, Loader2Icon, GlobeIcon } from 'lucide-react';
import { profile } from '../data/profile';
type Status = 'idle' | 'submitting' | 'success' | 'error';
interface FormState {
  name: string;
  email: string;
  organization: string;
  message: string;
}
const emptyForm: FormState = {
  name: '',
  email: '',
  organization: '',
  message: ''
};
export function ContactFooter() {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [status, setStatus] = useState<Status>('idle');
  const update = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (status === 'error') setStatus('idle');
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => {
      setStatus('success');
      setForm(emptyForm);
    }, 900);
  };
  return <footer id="contact" className="w-full bg-navy-950 text-white" data-id="element-11" data-mp="2u25sudwlf8r3">
      <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-12" data-mp="2u4ijrgkbwtga">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-13" data-mp="2pa4iwymwdu48">
          <div className="lg:col-span-7" data-id="element-14" data-mp="33zshvt7lwgos">
            <div className="h-px w-12 bg-gold-500" aria-hidden="true" data-id="element-15" data-mp="342ok3tqhlrj1" />
            <h2 className="mt-6 font-display text-3xl font-semibold tracking-tight sm:text-4xl" data-id="element-16" data-mp="2z0pfkgj0goki">
              Board Inquiry &amp; Direct Contact
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-white/65" data-id="element-17" data-mp="38zy7l3ubft48">
              Submissions route privately to William&apos;s inbox for board and advisory
              consideration, with a direct response within two business days.
            </p>

            <form onSubmit={handleSubmit} noValidate className="mt-10" data-id="element-18" data-mp="1enqi460rk4fu">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-id="element-19" data-mp="3drylintta8rq">
                <Field id="contact-name" label="Name" required value={form.name} onChange={update('name')} data-id="element-20" data-mp="3iop17a1pf2cm" />
                <Field id="contact-email" label="Email" type="email" required value={form.email} onChange={update('email')} data-id="element-21" data-mp="3sopqrdq62ep9" />
                <div className="sm:col-span-2" data-id="element-22" data-mp="5gm1dy0rjp29">
                  <Field id="contact-org" label="Organization" value={form.organization} onChange={update('organization')} data-id="element-23" data-mp="aaq9iklrm34x" />
                </div>
                <div className="sm:col-span-2" data-id="element-24" data-mp="k61nv1t9h8c5">
                  <label htmlFor="contact-message" className="block text-xs font-semibold uppercase tracking-[0.14em] text-white/55" data-id="element-25" data-mp="oahpurzr7vm3">
                    Message <span className="text-gold-500" data-id="element-26" data-mp="24ou7y4cd60s3">*</span>
                  </label>
                  <textarea id="contact-message" rows={5} required value={form.message} onChange={update('message')} className="mt-2.5 w-full resize-y border border-white/20 bg-navy-900 px-4 py-3 text-[15px] text-white placeholder-white/30 transition-colors duration-150 ease-exec focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500" placeholder="Outline the mandate, timeline, and scope." data-id="element-27" data-mp="1zq0w0gcq6hu1" />
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-5" data-id="element-28" data-mp="3b70vbvxhpwxk">
                <button type="submit" disabled={status === 'submitting'} className="inline-flex items-center gap-2 whitespace-nowrap bg-gold-500 px-6 py-3.5 text-sm font-semibold text-navy-950 transition-colors duration-150 ease-exec hover:bg-gold-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-200 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-950 disabled:cursor-not-allowed disabled:opacity-70" data-id="element-29" data-mp="36eqzy0dlw4nw">
                  {status === 'submitting' ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-30" data-mp="3emu0cyqz1at1" />
                      Sending
                    </> : 'Submit Inquiry'}
                </button>

                <div aria-live="polite" className="text-sm" data-id="element-31" data-mp="34vpnq5orq21d">
                  {status === 'success' ? <p className="inline-flex items-center gap-2 text-gold-200" data-id="element-32" data-mp="3sl0j17ncn484">
                      <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-33" data-mp="18z1k171g41k" />
                      Received — you&apos;ll hear back shortly.
                    </p> : null}
                  {status === 'error' ? <p className="text-red-300" data-id="element-34" data-mp="1gyzra3s7mbw8">
                      Please complete name, email, and message.
                    </p> : null}
                </div>
              </div>
            </form>
          </div>

          <div className="lg:col-span-5 lg:border-l lg:border-white/10 lg:pl-14" data-id="element-35" data-mp="12z6zs68uzrn7">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gold-500" data-id="element-36" data-mp="y0dn2g1ec4o3">
              Direct
            </p>
            <ul className="mt-6 space-y-4 text-[15px] text-white/75" data-id="element-37" data-mp="1c0f00tuxr2jx">
              <li className="flex items-center gap-3" data-id="element-38" data-mp="171213aqsi50z">
                <MapPinIcon className="h-4 w-4 flex-none text-gold-500" aria-hidden="true" data-id="element-39" data-mp="16zikfj181ryo" />
                {profile.location}
              </li>
              <li className="flex items-center gap-3" data-id="element-40" data-mp="2ig8vpgki59z5">
                <PhoneIcon className="h-4 w-4 flex-none text-gold-500" aria-hidden="true" data-id="element-41" data-mp="2ifpbft7dq9hy" />
                <a href={profile.phoneHref} className="transition-colors duration-150 ease-exec hover:text-white" data-id="element-42" data-mp="1g2197jiapy0c">
                  {profile.phone}
                </a>
              </li>
              <li className="flex items-center gap-3" data-id="element-43" data-mp="1p4kc23m30aml">
                <MailIcon className="h-4 w-4 flex-none text-gold-500" aria-hidden="true" data-id="element-44" data-mp="1u2lrmekatrtb" />
                <a href={`mailto:${profile.email}`} className="break-all transition-colors duration-150 ease-exec hover:text-white" data-id="element-45" data-mp="1u07xq91culnc">
                  {profile.email}
                </a>
              </li>
              <li className="flex items-start gap-3" data-id="element-46" data-mp="16czudwnhlmny">
                <GlobeIcon className="mt-0.5 h-4 w-4 flex-none text-gold-500" aria-hidden="true" data-id="element-47" data-mp="16c7zyp5mluz9" />
                {profile.availability}
              </li>
            </ul>

            <div className="mt-10 space-y-3 border-t border-white/10 pt-8" data-id="element-48" data-mp="dp40ezpay8uv">
              <a href={profile.linkedIn} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-between gap-3 border border-white/25 px-5 py-4 text-sm font-semibold text-white transition-colors duration-150 ease-exec hover:border-gold-500 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500" data-id="element-49" data-mp="inf751kaw38v">
                LinkedIn Profile
                <LinkedinIcon className="h-4 w-4 flex-none text-gold-500" aria-hidden="true" data-id="element-50" data-mp="3tbvep4vopi89" />
              </a>
              <a href={profile.resumeUrl} download className="inline-flex w-full items-center justify-between gap-3 border border-white/25 px-5 py-4 text-sm font-semibold text-white transition-colors duration-150 ease-exec hover:border-gold-500 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500" data-id="element-51" data-mp="3jjpr07cgtmyv">
                Download Executive Résumé (PDF)
                <DownloadIcon className="h-4 w-4 flex-none text-gold-500" aria-hidden="true" data-id="element-52" data-mp="30ldfiey30104" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-3 border-t border-white/10 pt-8 text-xs text-white/45 sm:flex-row sm:items-center sm:justify-between" data-id="element-53" data-mp="xb061bdfi04n">
          <p data-id="element-54" data-mp="jb7ghjdn1py9">© {new Date().getFullYear()} William R. Evans. All rights reserved.</p>
          <p data-id="element-55" data-mp="nbra4cz06bfn">Board Governance · Risk Management · Enterprise Growth</p>
        </div>
      </div>
    </footer>;
}
interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
}
function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  required
}: FieldProps) {
  return <div data-id="element-56" data-mp="4nmejssi1wrx">
      <label htmlFor={id} className="block text-xs font-semibold uppercase tracking-[0.14em] text-white/55" data-id="element-57" data-mp="4ncns2zmrll6">
        {label} {required ? <span className="text-gold-500" data-id="element-58" data-mp="3rral1g9geyid">*</span> : null}
      </label>
      <input id={id} type={type} value={value} onChange={onChange} required={required} className="mt-2.5 w-full border border-white/20 bg-navy-900 px-4 py-3 text-[15px] text-white placeholder-white/30 transition-colors duration-150 ease-exec focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500" data-id="element-59" data-mp="3rqhgmlxywl3e" />
    </div>;
}